import { useEffect, useMemo, useState } from "react";
import { Typography, Col, Flex, Row, Select, ConfigProvider, DatePicker, Tabs, Alert } from "antd";
import { showNotificationTopEnd } from "../../../util/utils";
import { callGetTicketsByTicketNumberAndAction, callMarkAsPaidTicket, callRemovePaidTicket, callResetTicket } from "../../../services/participantsService";
import { callGetLottosByStatusNoPageable } from "../../../services/lottoService";
import ManageTickets from "./TypeMovements/ManageTickets";
import { debounce } from "lodash";
import ReactDOM from "react-dom/client";
import esES from 'antd/locale/es_ES';
import Swal from "sweetalert2";
import dayjs from "dayjs";

import "./Movements.css";

const { Title } = Typography;


export default function Movements() {
    const [tickets, setTickets] = useState([]);
    const [searchOptions, setSearchOptions] = useState([]);
    const [selectedTickets, setSelectedTickets] = useState([]);
    const [loading, setLoading] = useState(false);
    const [lotto, setLotto] = useState(null);
    const [lottoList, setLottoList] = useState([]);
    const [selectedOption, setSelectedOption] = useState(1);
    const [searchText, setSearchText] = useState("");
    const [movementTypeSelected, setMovementTypeSelected] = useState(1);
    const [action, setAction] = useState("reserved");


    useEffect(() => {
        getLottoList();
    }, []);


    useEffect(() => {
        setSearchOptions([]);
        setTickets([]);
        setSelectedTickets([]);
        setSearchText("");
    }, [lotto, movementTypeSelected]);


    const handleDeleteTicket = (ticketNumber) => {
        // Quitar de tickets
        setTickets(prev => prev.filter(t => t.ticketNumber !== ticketNumber));

        // Quitar de selectedTickets
        setSelectedTickets(prev => prev.filter(num => num !== ticketNumber));

        // Quitar de searchOptions
        setSearchOptions(prev => prev.filter(opt => opt.value !== ticketNumber));

        // Actualizar searchText (si lo usas como lista separada por comas)
        setSearchText(prev => {
            const updated = prev
                .split(",")
                .map(s => s.trim())
                .filter(s => s !== "" && s !== ticketNumber.toString());
            return updated.join(",");
        });

    };


    const getLottoList = async () => {
        try {
            const response = await callGetLottosByStatusNoPageable({ status: ["ACTIVE", "CLOSED"] });
            if (response?.status === 200) {
                setLottoList(response.data);
            }
        } catch (err) {
            showNotificationTopEnd("error", "Error cargando sorteos");
        }
    };


    const getTickets = async (lottoId, value, action) => {
        if (!value) {
            setTickets([]);
            return;
        }
        setLoading(true);
        try {
            const response = await callGetTicketsByTicketNumberAndAction(lottoId, value, action);
            if (response?.status === 200) {
                setSearchOptions(response?.data?.map(ticket => ({
                    value: ticket.ticketNumber,
                    label: ticket.ticketNumber.toString(),
                    ticket
                })));
            } else {
                showNotificationTopEnd("error", "Error, por favor recarga la página");
            }
        } finally {
            setLoading(false);
        }
    };


    // eslint-disable-next-line
    const debounceFetcher = useMemo(() => debounce((lottoId, value, action) => { if (lottoId) getTickets(lottoId, value, action); }, 1000), []);


    const handleChangeTickets = (values, optionObjects) => {
        // Evitar duplicados en tickets
        setTickets(prevTickets => {
            const merged = [...prevTickets, ...optionObjects.map(opt => opt.ticket)];
            return merged.filter(
                (t, i, arr) => arr.findIndex(x => x.ticketNumber === t.ticketNumber) === i
            );
        });

        // Evitar duplicados en selectedTickets
        setSelectedTickets(prevSelected => [...new Set([...prevSelected, ...values])]);

        // Evitar duplicados en searchOptions
        setSearchOptions(prevOptions => {
            const merged = [...prevOptions, ...optionObjects];
            return merged.filter(
                (opt, i, arr) => arr.findIndex(x => x.value === opt.value) === i
            );
        });

        // Sincronizar el texto de búsqueda con lo que se seleccionó
        setSearchText(values.join(","));
    };


    const handleChangeRadio = (e) => {
        setSelectedOption(e.target.value);
    };


    const handleSearchTextArea = async () => {
        setLoading(true);
        try {
            const response = await callGetTicketsByTicketNumberAndAction(lotto?.lottoId, searchText, action);
            if (response?.status === 200) {
                setTickets(prevTickets => {
                    const merged = [...prevTickets, ...response.data];
                    return merged.filter(
                        (t, i, arr) => arr.findIndex(x => x.ticketNumber === t.ticketNumber) === i
                    );
                });

                setSelectedTickets(prevSelected => [
                    ...new Set([...prevSelected, ...response.data.map(ticket => ticket.ticketNumber)])
                ]);

                setSearchOptions(prevOptions => {
                    const newOptions = response.data.map(ticket => ({
                        value: ticket.ticketNumber,
                        label: ticket.ticketNumber.toString(),
                        ticket
                    }));
                    const merged = [...prevOptions, ...newOptions];
                    return merged.filter(
                        (opt, i, arr) => arr.findIndex(x => x.value === opt.value) === i
                    );
                });

                // Mantener sincronizado el texto de búsqueda
                setSearchText(searchText);
            }
        } finally {
            setLoading(false);
        }
    };


    const disabledDate = (current) => {
        return current && current > dayjs().endOf('day');
    };


    const handleMarkAsPaidTickets = () => {
        let purchaseDateValue = null;

        Swal.fire({
            title: "¿Estas seguro de marcar como pagados los tickets?",
            html: `
            <div style="text-align: left; margin-bottom: 15px;">
                Verifica <b>dos veces</b> los tickets y <b>agrega la fecha de pago</b> antes de confirmar.
            </div>
            <div id="ant-datepicker-container" style="width: 100%;"></div>
            <div id="date-error" style="color: #ff4d4f; text-align: left; font-size: 12px; margin-top: 5px; display: none;">
                Debes seleccionar una fecha de pago obligatoriamente.
            </div>
        `,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#a63827",
            cancelButtonColor: "#6c757d",
            cancelButtonText: "Cancelar",
            confirmButtonText: "Marcar como Pagado",
            didOpen: () => {
                // Renderizamos Ant Design dentro del div que creamos en el html de Swal
                const container = document.getElementById("ant-datepicker-container");
                const root = ReactDOM.createRoot(container);
                root.render(
                    <ConfigProvider locale={esES}>
                        <DatePicker
                            showTime={{ format: 'HH:mm:ss' }}
                            format="YYYY-MM-DD HH:mm:ss"
                            style={{ width: '100%' }}
                            placeholder="Seleccionar fecha y hora de pago"
                            disabledDate={disabledDate}
                            getPopupContainer={() => document.getElementById("ant-datepicker-container")}
                            onChange={(date) => {
                                purchaseDateValue = date ? date.format("YYYY-MM-DDTHH:mm:ss") : null;
                                document.getElementById("date-error").style.display = "none";
                            }}
                        />
                    </ConfigProvider>
                );
            },
            preConfirm: () => {
                // Validación: si no hay fecha, no deja continuar
                if (!purchaseDateValue) {
                    document.getElementById("date-error").style.display = "block";
                    return false;
                }
                return purchaseDateValue;
            }
        }).then((result) => {
            if (result.isConfirmed) {
                // Pasamos la fecha (result.value) a la función de envío
                sendMarkAsPaidTickets(result.value);
            }
        });
    };


    const handleClearTickets = () => {
        Swal.fire({
            title: "¿Estas seguro de liberar los tickets?",
            html: "Verifica <b>dos veces</b> los tickets que deseas liberar.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#17a2b8",
            cancelButtonColor: "#6c757d",
            cancelButtonText: "Cancelar",
            confirmButtonText: "Liberar"
        }).then((result) => {
            if (result.isConfirmed) {
                sendClearTickets();
            }
        });
    };


    const handleRemovePaidTickets = () => {
        Swal.fire({
            title: "¿Estas seguro de eliminar el pago?",
            html: "Verifica <b>dos veces</b> los tickets a los que le deseas eliminar el pago.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#595959",
            cancelButtonColor: "#a0a5aaff",
            cancelButtonText: "Cancelar",
            confirmButtonText: "Eliminar Pago"
        }).then((result) => {
            if (result.isConfirmed) {
                sendRemovePaidTickets();
            }
        });
    };


    const sendMarkAsPaidTickets = async (purchaseDateValue) => {
        setLoading(true);

        const data = {
            ticketIds: selectedTickets,
            date: purchaseDateValue
        };

        const response = await callMarkAsPaidTicket(lotto?.lottoId, data);
        if (response?.status === 200) {
            Swal.fire({
                title: "Pagados 🎟️",
                text: "Tus tickets han sido marcados como pagados exitosamente.",
                icon: "success",
                timer: 2000,
                showConfirmButton: false
            });
            setSelectedTickets([]);
            setSearchText("");
            setSearchOptions([]);
            setTickets([]);
        } else {
            showNotificationTopEnd("error", "Error, por favor recarga la pagina");
        }
        setLoading(false);
    };


    const sendClearTickets = async () => {
        setLoading(true);

        const response = await callResetTicket(lotto?.lottoId, selectedTickets);
        if (response?.status === 200) {
            Swal.fire({
                title: "Liberados 🎟️",
                text: "Tus tickets han sido liberados exitosamente.",
                icon: "success",
                timer: 2000,
                showConfirmButton: false
            });
            setSelectedTickets([]);
            setSearchText("");
            setSearchOptions([]);
            setTickets([]);
        } else {
            showNotificationTopEnd("error", "Error, por favor recarga la pagina");
        }
        setLoading(false);
    };


    const sendRemovePaidTickets = async () => {
        setLoading(true);

        const response = await callRemovePaidTicket(lotto?.lottoId, selectedTickets);
        if (response?.status === 200) {
            Swal.fire({
                title: "Eliminados 🎟️",
                text: "Los pagos de los tickets han sido eliminados exitosamente.",
                icon: "success",
                timer: 2000,
                showConfirmButton: false
            });
            setSelectedTickets([]);
            setSearchText("");
            setSearchOptions([]);
            setTickets([]);
        } else {
            showNotificationTopEnd("error", "Error, por favor recarga la pagina");
        }
        setLoading(false);
    };


    const items = [
        {
            key: "1",
            label: 'Marcar como Pagado',
            children: <ManageTickets
                lottoId={lotto?.lottoId}
                selectedOption={selectedOption}
                handleChangeRadio={handleChangeRadio}
                debounceFetcher={debounceFetcher}
                handleChangeTickets={handleChangeTickets}
                loading={loading}
                searchOptions={searchOptions}
                selectedTickets={selectedTickets}
                searchText={searchText}
                setSearchText={setSearchText}
                handleSearchTextArea={handleSearchTextArea}
                tickets={tickets}
                action={action}
                callMethod={handleMarkAsPaidTickets}
                buttonTitle="Marcar como Pagado"
                buttonClass="movements-paid-button"
                handleDeleteTicketList={handleDeleteTicket}
            />,
        },
        {
            key: "2",
            label: 'Liberar Tickets (Despreciados)',
            children: <ManageTickets
                lottoId={lotto?.lottoId}
                selectedOption={selectedOption}
                handleChangeRadio={handleChangeRadio}
                debounceFetcher={debounceFetcher}
                handleChangeTickets={handleChangeTickets}
                loading={loading}
                searchOptions={searchOptions}
                selectedTickets={selectedTickets}
                searchText={searchText}
                setSearchText={setSearchText}
                handleSearchTextArea={handleSearchTextArea}
                tickets={tickets}
                action={action}
                callMethod={handleClearTickets}
                buttonTitle="Liberar Tickets"
                buttonClass="movements-clear-button"
                handleDeleteTicketList={handleDeleteTicket}
            />,
        },
        {
            key: "3",
            label: 'Eliminar Pago',
            children: <ManageTickets
                lottoId={lotto?.lottoId}
                selectedOption={selectedOption}
                handleChangeRadio={handleChangeRadio}
                debounceFetcher={debounceFetcher}
                handleChangeTickets={handleChangeTickets}
                loading={loading}
                searchOptions={searchOptions}
                selectedTickets={selectedTickets}
                searchText={searchText}
                setSearchText={setSearchText}
                handleSearchTextArea={handleSearchTextArea}
                tickets={tickets}
                action={action}
                callMethod={handleRemovePaidTickets}
                buttonTitle="Eliminar Pago"
                buttonClass="movements-remove-button"
                handleDeleteTicketList={handleDeleteTicket}
            />,
        },
    ];


    return (
        <Flex vertical>
            <Row gutter={[16, 16]} justify="space-between" align="middle" style={{ marginBottom: 24 }}>
                <Col span="auto">
                    <Title level={3}>Movimientos en Tickets</Title>
                </Col>
            </Row>

            <Flex align="center">
                <Title style={{ marginTop: 10, minWidth: 100 }} level={4}>Selecciona el Sorteo</Title>
                <Select
                    showSearch
                    placeholder="Selecciona el sorteo"
                    filterOption={false}
                    style={{ width: "100%" }}
                    size="large"
                    options={lottoList.map(lotto => ({
                        value: lotto.lottoId,
                        label: `ID: ${lotto.lottoId} - #${lotto.lottoNumber} ${lotto.title} - ${lotto.status}`,
                    }))}
                    onChange={(value) => {
                        const selected = lottoList.find(l => l.lottoId === value);
                        setLotto(selected);
                    }}
                />
            </Flex>

            <br />
            {lotto ? (
                <Tabs
                    defaultActiveKey="1"
                    items={items}
                    type="card"
                    onChange={(key) => {
                        // Al cambiar, limpia todo gracias al useEffect
                        setMovementTypeSelected(key);
                        console.log("key", key);
                        setAction((key === "1" || key === "2") ? "reserved" : "paid");
                    }}
                />
            ) : (
                <Alert message="Por favor selecciona un sorteo para comenzar a gestionar los movimientos." type="info" showIcon />
            )}
        </Flex >
    );
}